import {useRef} from 'react'
import Zelect from '../../common/components/Zelect.js'
import {getState, useSelector} from '../../store.js'
import {
  dataImport2Selector,
  downloadPreImportErrorCSV,
  IGNORE_REGEX,
  startDataImportTask,
  updateDataImport,
} from './dataImportFunctions.js'
import {useZropdownElement} from '../../common/components/Zropdown.js'
import className from '../../common/className.js'
import ButtonPrimary from '../../common/components/Button/ButtonPrimary.js'
import {Count, Plural, PluralBlock} from '../../common/components/Plural.js'

function updateColumnMapping(index, option) {
  let {columnImportList} = dataImport2Selector(getState())

  columnImportList = [...columnImportList]
  columnImportList[index] = option.value

  updateDataImport({columnImportList})
}

function Display({option}) {
  return (
    <div className="flex flex--justify">
      {option.display}
      {option.missing &&
        (option.required ? (
          <span className="required">Missing required value</span>
        ) : (
          <em>Unmapped</em>
        ))}
    </div>
  )
}

function ListItem({
  option,
  index,
  form: {selectedIndex},
  values,
  onSelectInternal,
}) {
  const ref = useRef()
  const isSelected = index === selectedIndex
  useZropdownElement({ref, isSelected})

  return (
    <li ref={ref} className="list__item list__item--dropdown">
      <button
        className={className`btn--link list__link--dropdown ${{
          selected: values.includes(option.value),
          'list__link--dropdown-arrow-focus': isSelected,
        }}`}
        type="button"
        onClick={() => onSelectInternal(option)}
        disabled={option.disabled}
      >
        <Display option={option} />
      </button>
    </li>
  )
}

function ColumnMapping({column, index, columnOptions}) {
  const {columnImportList} = useSelector(dataImport2Selector)

  if (column.match(IGNORE_REGEX)) {
    return null
  }

  const selectedOption = columnOptions.find(
    (o) => o.value === (columnImportList[index] || ''),
  )

  return (
    <tr>
      <td className="table__td v-align-middle">
        <strong className="fs-01 lh-sm-md">{column}</strong>
      </td>
      <td className="table__td v-align-middle">
        <div className="align-center">
          <strong className="fs-02 lh-sm-md op-50">→</strong>
        </div>
      </td>
      <td className="table__td v-align-middle">
        <Zelect
          buttonClassName={className`mw-65 ${{
            'error-message': selectedOption?.duplicated,
          }}`}
          id={`data_import_columns__${index}`}
          key={index}
          options={columnOptions}
          addEmptyOption
          value={columnImportList[index] || ''}
          onChange={(option) => updateColumnMapping(index, option)}
          OptionComponent={ListItem}
          IfSingleSelected={Display}
        />
        {selectedOption?.duplicated && (
          <div>
            <small className="error error-message">Duplicate mapping</small>
          </div>
        )}
      </td>
    </tr>
  )
}

export default function ColumnMappings({columnOptions}) {
  const {
    columns,
    errors,
    mappingErrors,
    computeErrorsMarker,
    isStartingTask,
    serverError,
  } = useSelector(dataImport2Selector)

  const errorEntries = Object.entries(errors || {})
  const hasErrors = errorEntries.length > 0
  const canStartImport = hasErrors || mappingErrors?.length > 0

  return (
    <div className="wrap--row margin-top-25">
      <div className="medium-6 medium-centered columns">
        <div className="w-75 margin-bottom-30">
          <h3 className="fs-02 lh-md margin-bottom-5">
            <strong>Verify Column Value Mappings</strong>
          </h3>
          <p className="fs-01 lh-md margin-bottom-0">
            Before starting the data import, make sure the column values in your
            CSV map to the correct values in Ordoro.
          </p>

          {hasErrors && (
            <div className="alert alert--error margin-top-15">
              {errorEntries.map(([key, {messages, count, option}]) => (
                <PluralBlock key={key} count={count}>
                  <p className="fs-00 lh-md margin-bottom-0">
                    {option.display} - {[...messages].join(', ')} - <Count />{' '}
                    <Plural word="time" />
                  </p>
                </PluralBlock>
              ))}
              <div className="margin-top-7">
                <ButtonPrimary
                  size="x-sm"
                  isOutlined
                  onClick={() => downloadPreImportErrorCSV()}
                >
                  Download CSV with Errors
                </ButtonPrimary>
              </div>
            </div>
          )}
          {mappingErrors?.length > 0 && (
            <div className="alert alert--error margin-top-15">
              {mappingErrors.map((error, index) => (
                <p key={index} className="fs-00 lh-md margin-bottom-0">
                  {error}
                </p>
              ))}
            </div>
          )}
          {serverError && (
            <div className="alert alert--error margin-top-15">
              <p className="fs-00 lh-md margin-bottom-0">{serverError}</p>
            </div>
          )}
        </div>

        <table className="table fs-00 margin-bottom-30">
          <thead>
            <tr className="fs-n0">
              <th className="table__th">CSV Column Value</th>
              <th className="table__th w-10">&nbsp;</th>
              <th className="table__th">Ordoro Column Value Mapping</th>
            </tr>
          </thead>
          <tbody className="table__tbody table__tbody--lines v-align-middle">
            {columns.map((column, index) => (
              <ColumnMapping
                key={index}
                index={index}
                column={column}
                columnOptions={columnOptions}
              />
            ))}
          </tbody>
        </table>
        <div id="start_import" className="flex flex--justify margin-bottom-30">
          <div className="margin-auto">
            <ButtonPrimary
              size="lg"
              onClick={() => startDataImportTask()}
              isDisabled={canStartImport}
              isLoading={!!computeErrorsMarker || isStartingTask}
            >
              Start Importing Data
            </ButtonPrimary>
          </div>
        </div>
      </div>
    </div>
  )
}
